import { useMemo } from 'react';
import { History } from 'lucide-react';

import { useEnvironmentList } from '@/react/portainer/environments/queries/useEnvironmentList';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';
import { createPersistedStore } from '@@/datatables/types';
import { Card } from '@@/primitives/Card';
import { Icon } from '@@/Icon';

import { Workflow } from '../types';

import { CorrectionRow, historyColumns } from './HistoryColumns';

const tableKey = 'gitops-correction-history';

// Newest first, which is the order the engine keeps them in and the order a
// reader wants them.
const settingsStore = createPersistedStore(tableKey, {
  id: 'correctedAt',
  desc: true,
});

const label = 'Correction history';

/**
 * Lists what this workflow has put back, newest first.
 *
 * A correction leaves nothing behind in the status table - the resource matches
 * the source again, so the environment reports healthy - which is exactly why
 * it needs somewhere of its own. Without it, the only trace of a deployment
 * being held in place against somebody editing it would be a message that lasts
 * one interval.
 *
 * The engine keeps the most recent few per environment rather than everything,
 * because a deployment somebody edits by hand every interval would otherwise
 * grow a record with no end to it.
 */
export function HistoryTab({ workflow }: { workflow: Workflow }) {
  const tableState = useTableState(settingsStore, tableKey);
  const artifacts = useMemo(() => workflow.artifacts ?? [], [workflow]);

  const corrections = useMemo(
    () =>
      artifacts.flatMap((artifact) =>
        (artifact.environments ?? []).flatMap((environment) =>
          (environment.history ?? []).map((correction) => ({
            correction,
            environment,
            artifact,
          }))
        )
      ),
    [artifacts]
  );

  // One request for every environment in the table rather than one per row,
  // which is the difference between a page load and a stampede at fleet size.
  const endpointIds = useMemo(
    () => [
      ...new Set(
        corrections.map(({ environment }) => environment.endpointId ?? 0)
      ),
    ],
    [corrections]
  );

  const { environments: known } = useEnvironmentList(
    { endpointIds, pageLimit: 0 },
    { enabled: endpointIds.length > 0 }
  );

  const dataset: Array<CorrectionRow> = useMemo(() => {
    const names = new Map(known.map((env) => [env.Id, env.Name]));

    return corrections.map(({ correction, environment, artifact }) => {
      const environmentId = environment.endpointId ?? 0;

      return {
        // An environment corrects the same deployment more than once, so when
        // it happened is part of what makes a row itself.
        id: `${artifact.id}-${environmentId}-${correction.correctedAt}`,
        environmentId,
        name: names.get(environmentId) ?? `Environment ${environmentId}`,
        artifactName: artifact.name ?? '',
        correctedAt: correction.correctedAt ?? 0,
        revision: correction.revision,
        resources: correction.resources ?? [],
      };
    });
  }, [corrections, known]);

  return (
    // Card rather than the widget the table brings by default, matching the
    // overview: the widget is inset by mx-4 and sits on a lighter surface.
    <Card.Container aria-label={label}>
      <Datatable<CorrectionRow>
        noWidget
        columns={historyColumns(artifacts.length > 1)}
        dataset={dataset}
        settingsManager={tableState}
        // A node rather than a string, so the heading matches Card.Header
        // instead of the table's larger text and circular icon badge. The
        // accessible name comes from ariaLabel below.
        title={
          <span className="flex items-center gap-2">
            <Icon
              icon={History}
              size="md"
              className="shrink-0 opacity-70 th-highcontrast:text-white th-dark:text-gray-3"
            />
            <span className="text-sm leading-tight font-extrabold tracking-wide text-gray-9 th-highcontrast:text-white th-dark:text-white">
              {label}
            </span>
          </span>
        }
        ariaLabel={label}
        disableSelect
        emptyContentLabel="Nothing has been put back yet. Corrections appear here once an environment reconciles drift away."
        data-cy={`workflow-correction-history-${workflow.id}`}
        description={
          <p className="text-muted m-0 text-xs">
            What each environment put back to match its source. The engine keeps
            the most recent corrections per environment, not all of them.
          </p>
        }
      />
    </Card.Container>
  );
}
